import React, { useState, useEffect, useRef, useMemo } from "react";
import { Trophy, Handshake, RotateCcw, Home, Plane, ListOrdered, Swords } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import LeagueTable from "./LeagueTable";
import ShareResultButton from "./ShareResultButton";
import WinCelebration from "./WinCelebration";
import { ResultsPitch } from "./fpl/ResultsPitch";
import { buildLeagueTable } from "../data/gameData";

const CONFETTI_COLORS = ["#10B981", "#38BDF8", "#ffffff", "#16d878", "#e74c3c"];
const Confetti = () => (
  <div className="pointer-events-none fixed inset-0 overflow-hidden z-50">
    {Array.from({ length: 44 }).map((_, i) => {
      const size = 6 + Math.random() * 6;
      return (
        <span
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${Math.random() * 0.7}s`,
            animationDuration: `${1.6 + Math.random() * 1.3}s`,
          }}
        />
      );
    })}
  </div>
);

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

const Column = ({ name, you, summary, shown, done, win, tie, testid, avatar }) => {
  const listRef = useRef(null);
  const played = summary.fixtures.slice(0, shown);
  const w = played.filter((f) => f.res === "W").length;
  const d = played.filter((f) => f.res === "D").length;
  const l = played.filter((f) => f.res === "L").length;
  const pts = done ? summary.points : w * 3 + d;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [shown]);

  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-4 transition-colors ${
        done && win && !tie ? "border-[#10B981]/60 bg-[#10B981]/10" : "border-white/10 bg-white/[0.03]"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          {avatar && <img src={avatar} alt={name} className="w-6 h-6 rounded-full object-cover border border-white/15 shrink-0" />}
          <div className="font-display font-bold text-sm truncate">
            {name}
            {you ? " (you)" : ""}
          </div>
        </div>
        {done && (
          <div className="text-muted-38 text-[10px]">
            {summary.position}
            {ordinal(summary.position)}
          </div>
        )}
      </div>
      <div className="text-center py-1">
        <div className={`font-display font-extrabold leading-none ${done ? "text-[#10B981] text-5xl" : "text-white text-4xl"}`}>
          <span data-testid={`${testid}-points`}>{pts}</span>
          <span className="text-sm text-muted-38 font-semibold ml-1">pts</span>
        </div>
        <div className="text-muted-38 text-xs mt-1.5">
          {done ? `${summary.w}W ${summary.d}D ${summary.l}L` : `${w}W ${d}D ${l}L`}
        </div>
      </div>
      <div ref={listRef} className="mt-2 max-h-[180px] overflow-y-auto space-y-1 pr-1">
        {played.map((f) => (
          <div key={f.mw} className="flex items-center gap-1.5 rounded-md px-1.5 py-1 bg-white/[0.02]">
            <span className="text-muted-38 text-[9px] w-4 shrink-0">{f.mw}</span>
            {f.home ? (
              <Home className="w-2.5 h-2.5 text-slate-500 shrink-0" />
            ) : (
              <Plane className="w-2.5 h-2.5 text-slate-500 shrink-0" />
            )}
            <span
              className="w-4 h-4 rounded flex items-center justify-center text-[7px] font-bold text-white shrink-0"
              style={{ backgroundColor: f.color }}
            >
              {f.short}
            </span>
            <span className="flex-1 min-w-0 text-[11px] text-slate-300 truncate">{f.opp}</span>
            <span className="font-display font-bold text-white text-[11px] tabular-nums">
              {f.gf}-{f.ga}
            </span>
            <span
              className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold shrink-0 ${
                f.res === "W"
                  ? "bg-emerald-500/20 text-emerald-400"
                  : f.res === "D"
                  ? "bg-white/10 text-slate-300"
                  : "bg-red-500/20 text-red-400"
              }`}
            >
              {f.res}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Plays both players' seasons out side-by-side at the same time, then reveals the winner.
const LiveSimulation = ({ match, user, onPlayAgain, onRematch, onAcceptRematch, rematching, friendly = false, onComplete, againLabel }) => {
  const amCreator = match.creator.email === user.email;
  const me = amCreator ? match.creator : match.opponent;
  const them = amCreator ? match.opponent : match.creator;
  const themAvatar = them.avatar;
  const total = Math.max(me.summary.fixtures.length, them.summary.fixtures.length);

  const [shown, setShown] = useState(0);
  const [done, setDone] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [tableWho, setTableWho] = useState("me");
  const [pitchWho, setPitchWho] = useState("me");
  const timer = useRef(null);

  const myTable = useMemo(() => buildLeagueTable(me.summary, me.username), [me]);
  const theirTable = useMemo(() => buildLeagueTable(them.summary, them.username), [them]);

  useEffect(() => {
    setShown(0);
    setDone(false);
    timer.current = setInterval(() => {
      setShown((s) => {
        const n = s + 1;
        if (n >= total) {
          clearInterval(timer.current);
          setDone(true);
          return total;
        }
        return n;
      });
    }, 140);
    return () => clearInterval(timer.current);
  }, [total]);

  const tie = match.winner === "tie";
  const invitedMe = !!match.rematch_id && match.rematch_by_email !== user.email;
  const iWon = match.winner === user.email;

  const shareXI = (p) => p.board?.xi || [];
  const shareMe = { name: me.username, points: me.summary.points, w: me.summary.w, d: me.summary.d, l: me.summary.l, xi: shareXI(me) };
  const shareThem = { name: them.username, points: them.summary.points, w: them.summary.w, d: them.summary.d, l: them.summary.l, xi: shareXI(them) };

  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (done) onComplete && onComplete();
    if (done && iWon && !friendly && match.pot > 0) setCelebrate(true);
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  const skip = () => {
    clearInterval(timer.current);
    setShown(total);
    setDone(true);
  };

  return (
    <div className="relative z-10 max-w-2xl mx-auto px-4 py-6" data-testid="live-simulation">
      {done && iWon && !tie && <Confetti />}
      <WinCelebration open={celebrate} amount={match.pot} label={`Draft XI wager · you beat ${them.username}`} onClose={() => setCelebrate(false)} />
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold label-eyebrow text-[#10B981]">
          {done ? (
            tie ? <Handshake className="w-4 h-4" /> : <Trophy className="w-4 h-4" />
          ) : (
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
          )}
          {done ? "Full time" : `Simulating… Game ${shown}/${total}`}
        </div>
        <h1 className="font-display font-extrabold text-3xl mt-2" data-testid="sim-headline">
          {done ? (tie ? "It's a tie!" : iWon ? "You win!" : "You lost") : "Seasons in progress"}
        </h1>
        {done && (
          <p className="text-slate-400 text-sm mt-1">
            {friendly
              ? tie
                ? `Level on points — ${me.summary.points} each.`
                : iWon
                ? `You beat ${them.username}!`
                : `${them.username} edged it this time.`
              : tie
              ? `Both on ${me.summary.points} pts — your £${match.stake} stake was refunded.`
              : iWon
              ? `You took the £${match.pot} pot.`
              : `${them.username} took the £${match.pot} pot.`}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Column testid="sim-col-me" name={me.username} you summary={me.summary} shown={shown} done={done} win={iWon} tie={tie} />
        <Column testid="sim-col-them" name={them.username} avatar={themAvatar} summary={them.summary} shown={shown} done={done} win={!iWon} tie={tie} />
      </div>

      {done ? (
        <div className="mt-5 space-y-2">
          <div data-testid="results-pitch-section" className="mb-3">
            <div className="inline-flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 p-1 mb-2">
              {[
                { k: "me", label: "Your XI" },
                { k: "them", label: `${them.username}'s XI` },
              ].map((t) => (
                <button
                  key={t.k}
                  data-testid={`pitch-toggle-${t.k}`}
                  onClick={() => setPitchWho(t.k)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                    pitchWho === t.k ? "bg-[#10B981] text-[#0F172A]" : "text-slate-300 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="max-w-md mx-auto">
              {pitchWho === "me" ? (
                <ResultsPitch key="me" xi={me.board?.xi || []} total={me.summary.points} title="Your XI" testid="results-pitch-me" />
              ) : (
                <ResultsPitch key="them" xi={them.board?.xi || []} total={them.summary.points} title={`${them.username}'s XI`} testid="results-pitch-them" />
              )}
            </div>
          </div>
          <button
            data-testid="view-league-table-btn"
            onClick={() => setTableOpen(true)}
            className="btn-ghost w-full rounded-xl py-3 font-medium text-sm text-white flex items-center justify-center gap-2"
          >
            <ListOrdered className="w-4 h-4" /> View full league table
          </button>
          <ShareResultButton me={shareMe} them={shareThem} />
          {invitedMe && (
            <button
              data-testid="sim-accept-rematch-btn"
              onClick={onAcceptRematch}
              className="btn-green wager-glow w-full rounded-xl py-4 font-extrabold text-lg flex items-center justify-center gap-2"
            >
              <Swords className="w-5 h-5" /> {match.rematch_by} wants a rematch. Accept
            </button>
          )}
          {onRematch && !invitedMe ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="sim-rematch-btn"
                onClick={onRematch}
                disabled={rematching}
                className="btn-green rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Swords className="w-4 h-4" /> {rematching ? "…" : `Rematch ${them.username}${match.stake ? ` (${match.stake})` : ""}`}
              </button>
              <button
                data-testid="sim-play-again-btn"
                onClick={onPlayAgain}
                className="btn-ghost rounded-xl py-3.5 font-semibold text-white flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" /> Lobby
              </button>
            </div>
          ) : (
            <button
              data-testid="sim-play-again-btn"
              onClick={onPlayAgain}
              className="btn-green w-full rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> {againLabel || (friendly ? "Play again" : "Back to lobby")}
            </button>
          )}
        </div>
      ) : (
        <button
          data-testid="sim-skip-btn"
          onClick={skip}
          className="btn-ghost w-full rounded-xl py-3 text-sm font-medium text-slate-300 mt-4"
        >
          Skip to result
        </button>
      )}

      <Dialog open={tableOpen} onOpenChange={setTableOpen}>
        <DialogContent className="w-[calc(100%-2rem)] bg-[#1E293B] border border-white/10 text-white max-w-md p-4">
          <DialogTitle className="font-display font-bold text-lg">Final league table</DialogTitle>
          <DialogDescription className="sr-only">Final 20-team season standings.</DialogDescription>
          <div className="inline-flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 p-1 my-3">
            <button
              data-testid="table-toggle-me"
              onClick={() => setTableWho("me")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                tableWho === "me" ? "bg-[#10B981] text-[#0F172A]" : "text-slate-300 hover:text-white"
              }`}
            >
              Your table
            </button>
            <button
              data-testid="table-toggle-them"
              onClick={() => setTableWho("them")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                tableWho === "them" ? "bg-[#10B981] text-[#0F172A]" : "text-slate-300 hover:text-white"
              }`}
            >
              {them.username}
            </button>
          </div>
          <LeagueTable rows={tableWho === "me" ? myTable : theirTable} />
          <p className="text-muted-38 text-[11px] mt-3">
            {(() => {
              const pos = (tableWho === "me" ? myTable : theirTable).find((r) => r.isPlayer)?.pos;
              const who = tableWho === "me" ? "Your XI" : `${them.username}'s XI`;
              return `${who} finished ${pos}${ordinal(pos)} against the all-time league.`;
            })()}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveSimulation;
